import { React, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { redirect, useNavigate } from 'react-router-dom';
import { useMatch } from 'react-router-dom';
import { loggedInUserSelector } from 'redux/selectors';
import isEmpty from 'lodash/isEmpty';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIconM from 'assets/icons/icons-for-mobile/logout-icon-m';
import HomeIconM from 'assets/icons/icons-for-mobile/home-icon-m';
import DashboardIconM from 'assets/icons/icons-for-mobile/dashboard-icon-m';
import MyOrdersIconM from 'assets/icons/icons-for-mobile/my-orders-icon-m';
import MyCustomersIconM from 'assets/icons/icons-for-mobile/my-customers-icon-m';
import MyAttacheIconM from 'assets/icons/icons-for-mobile/my-attache-icon-m';
import MyContactsIconM from 'assets/icons/icons-for-mobile/my-contacts-icon-m';
import Share from 'components/share/share';
import CreateOverrideOrderModal from 'components/modals/create-override-order/create-override-order-modal';

import { logOut } from 'utils/auth';

import { useHeader1 } from './hooks';

const AccordianMenu = ({
  redirectTo,
  id,
  name,
  hasSubOption,
  subOption,
  setMenuValue,
  setShowSendInviteEmail,
  setShowOverrideOrderModal,
}) => {
  const navigate = useNavigate();

  if (id == 'lnk-home-0' || id == 'lnk-dashboard-0') {
    return (
      <Box
        sx={{
          height: '52px',
          backgroundColor: '#181818',
          padding: '0px 16px 0px 16px',
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center' height='100%'>
          {id == 'lnk-home-0' ? (
            <HomeIconM stroke='#ffff' />
          ) : id == 'lnk-dashboard-0' ? (
            <DashboardIconM />
          ) : (
            ''
          )}
          <Button
            variant='text'
            onClick={() => {
              navigate(redirectTo);
              setMenuValue('');
            }}
            sx={{
              color: '#FFFFFF',
              fontSIze: '14px',
              width: 'fit-content',
              paddingLeft: '0px',
              backgroundColor: '#181818',
              fontWeight: '400',
            }}
          >
            <Typography variant='subtitle1M' sx={{ color: '#FFFFFF' }}>
              {name}
            </Typography>
          </Button>
        </Stack>
      </Box>
    );
  }
  return (
    <Accordion elevation={0} disableGutters sx={{ backgroundColor: '#181818' }}>
      <AccordionSummary
        sx={{ height: '52px', backgroundColor: '#181818' }}
        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          {id == 'lnk-my-orders-0' ? (
            <MyOrdersIconM />
          ) : id == 'lnk-my-customers-0' ? (
            <MyCustomersIconM />
          ) : id == 'lnk-my-attache-0' ? (
            <MyAttacheIconM />
          ) : id == 'lnk-contact-0' ? (
            <MyContactsIconM />
          ) : null}
          <Typography variant='subtitle1M' sx={{ color: '#FFFFFF' }}>
            {name}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          height: '100%',
          width: '100%',
          backgroundColor: '#262626',
          borderRadius: '5px',
        }}
      >
        <Stack>
          {Array.isArray(subOption) &&
            subOption?.map((option) => {
              if (option.isSection) {
                return (
                  Array.isArray(option.section.subSection) &&
                  option.section.subSection.map((section, i) => {
                    return i == 0 ? (
                      <>
                        <Typography
                          variant='subtitle1M'
                          sx={{
                            color: '#FFFFFF',
                            fontWeight: '900',
                            padding: '5px 0px 5px 0px',
                            opacity: '0.9',
                          }}
                        >
                          {option.section.title}
                        </Typography>
                        <Button
                          key={option.id}
                          variant='text'
                          onClick={() => {
                            navigate(section?.redirectTo);
                            setMenuValue('');
                          }}
                          sx={{
                            width: 'fit-content',
                            color: '#ffff',
                            fontSize: '14px',
                            opacity: '0.9',
                            fontWeight: '400',
                          }}
                        >
                          {section.name}
                        </Button>
                      </>
                    ) : (
                      <Button
                        key={option.id}
                        variant='text'
                        onClick={() => {
                          navigate(section?.redirectTo);
                          setMenuValue('');
                        }}
                        sx={{
                          width: 'fit-content',
                          color: '#ffff',
                          fontSize: '14px',
                          opacity: '0.9',
                          fontWeight: '400',
                        }}
                      >
                        {section.name}
                      </Button>
                    );
                  })
                );
              } else {
                return (
                  <Button
                    key={option.id}
                    variant='text'
                    sx={{
                      width: 'fit-content',
                      color: '#ffff',
                      fontSize: '14px',
                      opacity: '0.9',
                      fontWeight: '400',
                    }}
                    onClick={() => {
                      if (option.id === 'lnk-my-invites-0') {
                        setShowSendInviteEmail(true);
                      } else if (option.id === 'lnk-override-order-0') {
                        setShowOverrideOrderModal(true);
                      } else {
                        navigate(option?.redirectTo);
                        setMenuValue('');
                      }
                    }}
                  >
                    {option.name}
                  </Button>
                );
              }
            })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
AccordianMenu.propTypes = {
  redirectTo: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  hasSubOption: PropTypes.bool,
  subOption: PropTypes.array,
  setMenuValue: PropTypes.func,
  setShowSendInviteEmail: PropTypes.func,
  setShowOverrideOrderModal: PropTypes.func,
};

AccordianMenu.defaultProps = {
  redirectTo: '',
  id: '',
  name: '',
  hasSubOption: '',
  subOption: '',
  setMenuValue: () => {},
  setShowSendInviteEmail: () => {},
  setShowOverrideOrderModal: () => {},
};
const Header1M = ({ setMenuValue }) => {
  //flag to check if the app is on a marketplace page
  const isMarketplace = useMatch(`/marketplace/*`);
  const { headerMenu, showMenuItemsOnScroll, activeDraftSuggestion } =
    useHeader1({ isMarketplace });
  const [showSendInviteEmail, setShowSendInviteEmail] = useState(false);
  const [showOverrideOrderModal, setShowOverrideOrderModal] = useState(false);
  const user = useSelector(loggedInUserSelector);
  const theme = useTheme();
  //   const welcomeBarConfig = useSelector(welcomeBarDataSelector);
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        backgroundColor: '#000000',
        // backgroundColor: 'white',
        width: '100%',
        height: '100vh',
        position: 'fixed',
        top: '0px',
        left: '0px',
        zIndex: 999,
        color: 'white',
        // overflow: 'scroll',
        // paddingBottom: '56px',
      }}
    >
      {showSendInviteEmail ? (
        <Share
          open={showSendInviteEmail}
          setOpen={setShowSendInviteEmail}
          sendinviteEmail={true}
        />
      ) : null}
      {showOverrideOrderModal ? (
        <CreateOverrideOrderModal
          open={showOverrideOrderModal}
          setOpen={(bool) => {
            setShowOverrideOrderModal(bool);
            if (!bool) setMenuValue('');
          }}
        />
      ) : null}
      <Accordion
        disableGutters
        elevation={0}
        expanded={true}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: '#181818',
          opacity: '100%',
        }}
      >
        <AccordionSummary
          sx={{ height: '92px', backgroundColor: '#000000' }}
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
        >
          <Stack direction='row' spacing={2}>
            {user.profile_pic ? (
              <Avatar
                src={user.profile_pic}
                sx={{
                  width: '54px',
                  height: '54px',
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: '54px',
                  height: '54px',
                  padding: '15px',
                }}
              >
                {user.name
                  ? user.name.split(' ')[0]?.charAt(0)?.toUpperCase() +
                    user.name.split(' ')[1]?.charAt(0)?.toUpperCase()
                  : null}
              </Avatar>
            )}
            <Stack spacing={0.3}>
              <Typography variant='subtitle1M' sx={{ color: '#FFFFFF' }}>
                {user.name}
              </Typography>
              <Button
                variant='text'
                color='primary'
                sx={{
                  padding: '0px 0px 0px 0px',
                  height: 'fit-content',
                  width: 'fit-content',
                }}
                onClick={() => {
                  logOut();
                }}
              >
                <Stack
                  direction='row'
                  spacing={1}
                  sx={{
                    height: '100%',
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <LogoutIconM stroke={`${theme.palette.primary.main}`} />
                  <Typography
                    variant='subtitle1M'
                    sx={{ color: 'primary.main' }}
                  >
                    Sign Out
                  </Typography>
                </Stack>
              </Button>
              {headerMenu?.rightHeaderMenu?.map((menuItem) => {
                if (menuItem?.id == 'lft-lnk-my-account-0') {
                  if (menuItem?.hasSubOption) {
                    return menuItem?.subOption?.map((subMenu, i) => {
                      if (subMenu?.id == 'lft-lnk-my-account-2') {
                        return (
                          <Button
                            key={subMenu?.id}
                            variant='text'
                            color='primary'
                            sx={{
                              padding: '0px 0px 0px 0px',
                              height: 'fit-content',
                              width: 'fit-content',
                            }}
                            onClick={() => {
                              window.open(subMenu?.redirectTo, '_blank');
                            }}
                          >
                            <Stack
                              direction='row'
                              spacing={1}
                              sx={{
                                height: '100%',
                                width: '100%',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                variant='subtitle1M'
                                sx={{ color: 'primary.main' }}
                              >
                                {subMenu?.name}
                              </Typography>
                            </Stack>
                          </Button>
                        );
                      }
                    });
                  }
                }
              })}
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            // backgroundColor: '#262626',
            // opacity: '0%',
            padding: '10px 0px 0px 0px',
            flexGrow: 1,
            height: '90vh',
            overflow: 'scroll',
            paddingBottom: '70px',
          }}
        >
          <Stack
            sx={{
              flexGrow: 1,
              backgroundColor: '#181818',
              padding: '0px 20px 0px 10px',
            }}
          >
            {headerMenu?.leftHeaderMenu?.map((item, i) => (
              <AccordianMenu
                key={i}
                id={item.id}
                name={item?.name?.toUpperCase()}
                redirectTo={item.redirectTo}
                hasSubOption={item.hasSubOption}
                subOption={item.subOption}
                setMenuValue={setMenuValue}
                setShowSendInviteEmail={setShowSendInviteEmail}
                setShowOverrideOrderModal={setShowOverrideOrderModal}
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
Header1M.propTypes = {
  setMenuValue: PropTypes.func,
};

Header1M.defaultProps = {
  setMenuValue: () => {},
};
export default Header1M;
